// plugin-sandbox.js - main-thread side of the third-party plugin sandbox.
//
// Spawns the hardened module worker (js/plugin-worker.js), MEDIATES its only capability
// (engine() runs ONLY an allow-listed GDBS engine; nothing else crosses), forwards log(),
// and enforces a wall-clock WATCHDOG - an overrunning plugin is terminated, it cannot hang
// or mine. The worker has no network/DOM by construction (see plugin-worker.js).

const WORKER_URL = '/js/plugin-worker.js';

// createSandbox(code, { engine, allow, onLog, timeoutMs })
//   engine(name, req) -> Promise<result>  : the REAL GDBS host engine call (main thread)
//   allow: string[]                        : engine names the plugin may invoke
//   onLog(label, data)                     : forwarded gdbs.log()
// returns { init(): Promise<schema>, run(params): Promise<result>, dispose() }
export function createSandbox(code, opts) {
  opts = opts || {};
  const engine = typeof opts.engine === 'function' ? opts.engine : null;
  const allow = Array.isArray(opts.allow) ? opts.allow : null; // null = allow any known engine
  const onLog = typeof opts.onLog === 'function' ? opts.onLog : function () {};
  const timeoutMs = opts.timeoutMs || 20000;

  // gdbslog module name for this plugin sandbox - prefer the explicit pluginId so
  // the audit record is attributable to the plugin, else a generic label.
  const logModule = 'plugin:' + (opts.pluginId || opts.id || opts.name || 'sandbox');

  let worker = new Worker(WORKER_URL, { type: 'module' });
  let alive = true;
  const inflight = new Map(); // 'init' | 'run' -> { resolve, reject, timer }

  function settle(kind, err, val) {
    const w = inflight.get(kind); if (!w) return;
    inflight.delete(kind); clearTimeout(w.timer);
    if (err) w.reject(err instanceof Error ? err : new Error(String(err))); else w.resolve(val);
  }

  worker.onmessage = async (e) => {
    const m = e.data || {};
    if (m.type === 'engine') {
      // MEDIATE: only an allow-listed real GDBS engine ever runs; the plugin cannot reach
      // anything the host does not explicitly grant.
      let result = null, error = null;
      try {
        if (!engine) throw new Error('engine capability unavailable in this context');
        if (allow && allow.indexOf(m.name) === -1) throw new Error('engine "' + m.name + '" is not permitted for this plugin');
        result = await engine(m.name, m.req);
      } catch (err) { error = String((err && err.message) || err); }
      if (alive) { try { worker.postMessage({ type: 'engineResult', callId: m.callId, result: result, error: error }); } catch (e2) {} }
      return;
    }
    if (m.type === 'log') { try { onLog(m.label, m.data); } catch (e2) {} return; }
    if (m.type === 'init') { m.error ? settle('init', new Error(m.error)) : settle('init', null, m.schema); return; }
    if (m.type === 'result') { m.error ? settle('run', new Error(m.error)) : settle('run', null, m.result); return; }
  };
  worker.onerror = (e) => {
    const err = new Error('plugin sandbox error: ' + ((e && e.message) || 'worker crashed'));
    for (const k of [...inflight.keys()]) settle(k, err);
  };
  worker.onmessageerror = () => {
    const err = new Error('plugin sandbox: result was not cloneable (a plugin must RETURN data, not functions/DOM)');
    for (const k of [...inflight.keys()]) settle(k, err);
  };

  function call(kind, payload, ms) {
    return new Promise((resolve, reject) => {
      if (!alive) return reject(new Error('sandbox disposed'));
      const timer = setTimeout(() => {
        settle(kind, new Error('plugin timed out after ' + (ms || timeoutMs) + ' ms - sandbox terminated'));
        dispose();
      }, ms || timeoutMs);
      inflight.set(kind, { resolve, reject, timer });
      try { worker.postMessage(Object.assign({ type: kind }, payload)); } catch (e) { settle(kind, e); }
    });
  }

  function dispose() { alive = false; try { worker.terminate(); } catch (e) {} for (const k of [...inflight.keys()]) settle(k, new Error('sandbox disposed')); }

  // The plugin runs in the worker, where gdbslog can't see it (no window). Log
  // init/run here on the main thread via gdbslog.time so the run is audited with
  // real duration + result telemetry. time() handles the async worker promise;
  // guard on window.gdbslog so this is safe if ever evaluated in worker scope.
  function logged(action, thunk) {
    const lg = (typeof window !== 'undefined' && window.gdbslog) ? window.gdbslog.getLogger(logModule) : null;
    return lg ? lg.time(action, thunk) : thunk();
  }

  return {
    init() { return logged('init', () => call('init', { code: code }, Math.min(8000, timeoutMs))); },
    run(params) { return logged('run', () => call('run', { code: code, params: params }, timeoutMs)); },
    dispose: dispose,
  };
}
